import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { getOidcConfig } from '../../../auth/config'
import { startLogin, startLogout } from '../../../auth/oidc'
import { claimOidcReauthorization, clearOidcOverride, clearOidcRetry, clearSession, loadOidcOverride, loadOidcRetry, loadSession, saveIamNewUserHint, saveSession, setFlashToast, takeFlashToast } from '../../../auth/state'
import { useToast } from '../../../components/Toast'
import chamiiLogo from '../../../assets/images/logo.png'

function loginError(error) {
  const code = String(error?.message || error)
  return code === 'oidc_automatic_login_blocked'
    ? 'Automatic sign-in stopped because the previous attempt did not complete. Retry manually after the sign-in service is available.'
    : code
}

function normalizeHintToken(s) {
  return String(s || '')
    .trim()
    .slice(0, 200)
    .replace(/[^a-zA-Z0-9:_\\-\\.]/g, '')
}

function parseAutoMode(qs) {
  const raw = String(qs.get('auto') || '').trim().toLowerCase()
  if (raw === '0' || raw === 'false' || raw === 'off') return false
  return true
}

function localDevAccessToken() {
  const payload = btoa(JSON.stringify({
    sub: 'dev-admin',
    typ: 'access',
    aud: 'chamii-main',
    client_id: 'chamii-main',
    azp: 'chamii-main',
    exp: Math.floor(Date.now() / 1000) + 8 * 60 * 60,
    roles: ['ADMIN'],
  })).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  return `local.${payload}.dev`
}

function useLoginParams() {
  const location = useLocation()
  const qs = new URLSearchParams(location.search)
  const n = qs.get('next') || '/monitor'
  const next = n.startsWith('/') && !n.startsWith('//') && !n.includes('\\') ? n : '/monitor'
  const auto = parseAutoMode(qs)
  const iamNewUser = qs.get('iam_new_user') === '1' || qs.get('iam_new_user') === 'true'
  const iamSub = normalizeHintToken(qs.get('iam_sub') || '')
  const iamApp = normalizeHintToken(qs.get('iam_app') || '')
  // Force fresh login - clear any stale session
  const fresh = qs.get('fresh') === '1' || qs.get('fresh') === 'true'
  const reauth = qs.get('reauth') === '1' || qs.get('reauth') === 'true'
  return { next, auto, iamNewUser, iamSub, iamApp, fresh, reauth }
}

export default function Login() {
  const navigate = useNavigate()
  const { next, auto, iamNewUser, iamSub, iamApp, fresh, reauth } = useLoginParams()
  const cfg = useMemo(() => getOidcConfig(), [])
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  const { toast } = useToast()
  const oidcOverride = loadOidcOverride()
  const oidcRetry = loadOidcRetry()

  const [existing, setExisting] = useState(() => loadSession())

  // Clear stale session if fresh=1
  useEffect(() => {
    if (!fresh) return
    clearSession()
    setExisting(null)
  }, [fresh])

  const localLogin = window.location.origin === 'https://admin-dev.chamii.com' && cfg?.provider === 'iam'
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [mode, setMode] = useState('idle') // idle | redirecting
  const startedRef = useRef(false)

  useEffect(() => {
    const t = takeFlashToast()
    if (t) toast(t)
  }, [toast])

  useEffect(() => {
    if (!iamNewUser || !iamSub) return
    saveIamNewUserHint({ sub: iamSub, app: iamApp || undefined, at: Date.now() })
  }, [iamApp, iamNewUser, iamSub])

  // Auto-redirect to IAM login if auto mode is enabled and no valid session
  useEffect(() => {
    // Skip if already started, has existing session, no config, or auto disabled
    if (localLogin) return
    if (startedRef.current) return
    if (existing) return
    if (!cfg) return
    if (!auto) return
    if (reauth && !claimOidcReauthorization()) {
      startedRef.current = true
      setErr(loginError(new Error('oidc_automatic_login_blocked')))
      return
    }

    startedRef.current = true
    setErr('')
    setBusy(true)
    setMode('redirecting')

    // Directly start login - no health check needed for trusted domains
    startLogin(next).catch((e) => {
      setErr(loginError(e))
      setBusy(false)
      setMode('idle')
    })
  }, [auto, cfg, existing, next, reauth, localLogin])

  if (existing) {
    return (
      <div className="chamii-viewport chamii-safe-top chamii-safe-bottom bg-dots chamii-auth-shell flex items-center justify-center p-6">
        <div className="chamii-login-card w-full max-w-lg bg-brand-surface">
          <div className="chamii-login-content px-6 sm:px-10 py-10">
            <div className="chamii-pixel-mark w-14 h-14 flex items-center justify-center">
              <span className="text-3xl font-black text-brand-textDark">✓</span>
            </div>
            <div className="mt-7 text-4xl font-black tracking-tight text-brand-textDark">Signed in</div>
            <div className="mt-3 text-brand-text text-sm leading-relaxed">Continue to the console.</div>
            <div className="mt-8 grid gap-3">
              <button
                type="button"
                className="chamii-login-primary w-full rounded-sm bg-brand-primary text-white font-semibold py-4"
                onClick={() => navigate(next, { replace: true })}
              >
                Continue
              </button>
              <button
                type="button"
                className="w-full rounded-sm bg-white text-brand-textDark font-semibold py-3 border border-violet-200 hover:bg-violet-50 transition-colors"
                onClick={() => {
                  startedRef.current = true
                  setFlashToast({ kind: 'success', title: 'Signed out', message: 'Logout successful.' })
                  startLogout()
                  setExisting(null)
                }}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const headline = mode === 'redirecting' ? 'Redirecting…' : 'Your workspace, connected.'
  const subline = mode === 'redirecting' ? 'Redirecting to Chamii IAM…' : localLogin ? 'Sign in with your Chamii account.' : 'Sign in via Chamii IAM to continue.'

  return (
    <div className="chamii-viewport chamii-safe-top chamii-safe-bottom bg-dots chamii-auth-shell flex items-center justify-center p-6">
      <div className="chamii-login-card w-full max-w-lg bg-brand-surface">
        <div className="chamii-login-content px-6 sm:px-10 py-10">
          <div className="chamii-pixel-mark w-14 h-14 flex items-center justify-center">
            {busy ? (
              <div className="w-10 h-10 border-[5px] border-brand-textDark border-t-transparent rounded-full animate-spin" />
            ) : (
              <img src={chamiiLogo} alt="Chamii" className="h-9 w-9 object-contain" />
            )}
          </div>

          <p className="chamii-eyebrow mt-8">CHAMII / ADMIN CONSOLE</p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-brand-textDark">{headline}</h1>
          <div className="mt-3 text-brand-text text-sm leading-relaxed">{subline}</div>

          {!cfg && (
            <div className="mt-6 rounded-2xl border-2 border-brand-pink/20 bg-brand-pink/10 px-4 py-3 text-brand-pinkDark font-extrabold text-sm">
              Missing OIDC config on this build.
            </div>
          )}
          {err && (
            <div role="alert" className="mt-6 rounded-2xl border-2 border-brand-pink/20 bg-brand-pink/10 px-4 py-3 text-brand-pinkDark font-extrabold text-sm break-words">
              {err}
            </div>
          )}

          {localLogin && <form className="mt-8 grid gap-3" onSubmit={async event => {
            event.preventDefault()
            if (busy) return
            setErr('')
            setBusy(true)
            try { await startLogin(next, { manual: true, credentials: { identifier, password } }) }
            catch (error) { setErr(loginError(error)) }
            finally { setPassword(''); setBusy(false) }
          }}>
            <label htmlFor="login-identifier">Email or username</label>
            <input id="login-identifier" autoComplete="username" required maxLength={320} value={identifier} onChange={event => setIdentifier(event.target.value)} className="w-full rounded-sm border p-3" />
            <label htmlFor="login-password">Password</label>
            <input id="login-password" type="password" autoComplete="current-password" required maxLength={1024} value={password} onChange={event => setPassword(event.target.value)} className="w-full rounded-sm border p-3" />
            <button disabled={busy} className="chamii-login-primary w-full rounded-sm bg-brand-primary text-white font-semibold py-4">{busy ? 'Signing in…' : 'Sign in'}</button>
          </form>}
          <div className="mt-8 grid gap-3">
            <button
              type="button"
              disabled={!cfg || busy}
              className={`chamii-login-primary w-full rounded-sm bg-brand-primary text-white font-semibold py-4 ${
                !cfg || busy ? 'opacity-60 cursor-not-allowed shadow-none' : ''
              }`}
              onClick={() => {
                if (!cfg || busy) return
                setErr('')
                setBusy(true)
                setMode('redirecting')
                startLogin(next, { manual: true }).catch((e) => {
                  setErr(loginError(e))
                  setBusy(false)
                  setMode('idle')
                })
              }}
            >
              {localLogin ? 'Google and other sign-in options' : 'Continue with Chamii IAM'}
            </button>

            {isLocalhost && (
              <button
                type="button"
                className="w-full rounded-sm bg-white text-brand-textDark font-semibold py-3 border border-violet-200 hover:bg-violet-50 transition-colors"
                onClick={() => {
                  saveSession({ tokens: { accessToken: localDevAccessToken() }, obtainedAt: Date.now(), user: { preferred_username: 'dev-admin', roles: ['ADMIN'] } })
                  navigate(next, { replace: true })
                }}
              >
                Dev mode enter
              </button>
            )}

            {err && (
              <button
                type="button"
                className="w-full rounded-sm bg-white text-brand-textDark font-semibold py-3 border border-violet-200 hover:bg-violet-50 transition-colors"
                onClick={() => {
                  clearSession()
                  window.location.replace('/login?fresh=1&auto=0')
                }}
              >
                Clear session & retry
              </button>
            )}

            {err && (oidcOverride || (oidcRetry && oidcRetry.count > 0)) && (
              <button
                type="button"
                className="w-full rounded-sm bg-white text-brand-textDark font-semibold py-3 border border-violet-200 hover:bg-violet-50 transition-colors"
                onClick={() => {
                  clearSession()
                  clearOidcOverride()
                  clearOidcRetry()
                  window.location.replace('/login?fresh=1&auto=0')
                }}
              >
                Reset login config
              </button>
            )}
          </div>

          <div className="mt-8 chamii-eyebrow">SECURE SIGN-IN · CHAMII IAM</div>
        </div>
      </div>
    </div>
  )
}
